import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import twilioClient from '../../utils/twilioClient';

const { FiPhone, FiRefreshCw } = FiIcons;

const TwilioCallsConsole = () => {
  const [numbers, setNumbers] = useState([]);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calling, setCalling] = useState(false);
  
  const [formData, setFormData] = useState({
    to: '',
    from: '',
    sayMessage: 'This is a test call from my Twilio control panel.',
    twimlUrl: ''
  });

  const [filters, setFilters] = useState({
    status: '',
    dateRange: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [numbersData, callsData] = await Promise.all([
        twilioClient.getNumbers(),
        twilioClient.getCalls()
      ]);
      setNumbers(numbersData.numbers || []);
      setCalls(callsData.calls || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInitiateCall = async (e) => {
    e.preventDefault();
    if (!formData.to) return;

    setCalling(true);
    try {
      const callData = await twilioClient.initiateCall(formData);
      alert(`Call initiated! SID: ${callData.sid}`);
      setFormData({
        to: '',
        from: '',
        sayMessage: 'This is a test call from my Twilio control panel.',
        twimlUrl: ''
      });
      loadData(); // Refresh calls
    } catch (error) {
      alert(`Failed to initiate call: ${error.message}`);
    } finally {
      setCalling(false);
    }
  };

  const filteredCalls = calls.filter(call => {
    if (filters.status && call.status !== filters.status) return false;
    // Add date range filtering logic here if needed
    return true;
  });

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-8">
      {/* Outbound Call Form */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h2 className="text-xl font-semibold mb-6">Initiate Outbound Call</h2>
        
        <form onSubmit={handleInitiateCall} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To (E.164 format)
              </label>
              <input
                type="tel"
                value={formData.to}
                onChange={(e) => setFormData({ ...formData, to: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="+61412345678"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From
              </label>
              <select
                value={formData.from}
                onChange={(e) => setFormData({ ...formData, from: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Default Number</option>
                {numbers.map((number) => (
                  <option key={number.sid} value={number.phoneNumber}>
                    {number.phoneNumber} ({number.friendlyName})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message to Say (optional)
            </label>
            <textarea
              value={formData.sayMessage}
              onChange={(e) => setFormData({ ...formData, sayMessage: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Text that will be spoken during the call"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              TwiML URL (optional)
            </label>
            <input
              type="url"
              value={formData.twimlUrl}
              onChange={(e) => setFormData({ ...formData, twimlUrl: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://example.com/twiml"
            />
            <p className="text-sm text-gray-500 mt-1">
              If provided, this URL will be used instead of the message above
            </p>
          </div>

          <button
            type="submit"
            disabled={calling || !formData.to}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <SafeIcon icon={calling ? FiRefreshCw : FiPhone} className={`mr-2 ${calling ? 'animate-spin' : ''}`} />
            {calling ? 'Initiating...' : 'Start Call'}
          </button>
        </form>
      </div>

      {/* Calls History */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Calls History</h2>
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
            >
              <SafeIcon icon={FiRefreshCw} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="queued">Queued</option>
              <option value="ringing">Ringing</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="busy">Busy</option>
              <option value="no-answer">No Answer</option>
              <option value="failed">Failed</option>
              <option value="canceled">Canceled</option>
            </select>
            <input
              type="date"
              value={filters.dateRange}
              onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  From
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Direction
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Start Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCalls.map((call) => (
                <tr key={call.sid}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {call.from}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {call.to}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      call.direction === 'inbound' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {call.direction}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      call.status === 'completed' ? 'bg-green-100 text-green-800' :
                      call.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                      call.status === 'failed' ? 'bg-red-100 text-red-800' :
                      call.status === 'no-answer' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {call.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {call.startTime ? new Date(call.startTime).toLocaleString() : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDuration(call.duration)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {call.price ? `$${call.price}` : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredCalls.length === 0 && !loading && (
            <div className="text-center py-8 text-gray-500">
              No calls found
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwilioCallsConsole;